class ProductSheetTable {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.scrollContainer = wrapper.querySelector(".product-sheet-table-scroll");
    this.table = wrapper.querySelector(".product-sheet-table");
    this.tbody = this.table.querySelector("tbody");

    this.productTitle = this.scrollContainer.dataset.productTitle;

    this.currentSort = { column: null, direction: "asc" };
    this.allData = [];

    this.overlay = document.querySelector(".product-sheet-table__overlay");
    this.overlayImage = this.overlay.querySelector(
      ".product-sheet-table__overlay-image"
    );
    this.closeButton = this.overlay.querySelector(
      ".product-sheet-table__close-button"
    );

    this.init();
  }

  async init() {
    try {
      this.setupEventListeners();
      await this.fetchData();
    } catch (error) {
      console.error("Failed to initialize product sheet table:", error);
      this.showError("Failed to load product data. Please try again later.");
    }
  }

  async fetchData() {
    try {
      const response = await fetch(
        "https://apiconnector-erdem9.replit.app/api/sheet-data"
      );

      if (!response.ok) {
        throw new Error("Failed to fetch data from API");
      }

      const data = await response.json();
      this.processData(data);
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  }

  processData(data) {
    if (!Array.isArray(data)) {
      console.error("Data is not an array:", data);
      return;
    }

    // Filter data for current product
    this.allData = data.filter((item) => {
      const itemName = (item.name || "").toLowerCase().trim();
      const productTitle = this.productTitle.toLowerCase().trim();

      return itemName === productTitle;
    });

    if (this.allData.length > 0) {
      this.renderTable();
    } else {
      this.showError("Out of stock for this product: " + this.productTitle);
    }
  }

  setupEventListeners() {
    this.table.querySelectorAll("th[data-sort]").forEach((th) => {
      th.addEventListener("click", () => this.handleSort(th.dataset.sort));
    });

    this.tbody.addEventListener("click", (e) => {
      const thumbnail = e.target.closest(".product-sheet-table__thumbnail");
      if (thumbnail) {
        this.showOverlay(thumbnail.dataset.fullImage);
      }
    });

    this.closeButton.addEventListener("click", () => this.closeOverlay());
    this.overlay.addEventListener("click", (e) => {
      // Close if the click is on the overlay or overlay container, but not on the image itself
      if (
        e.target === this.overlay ||
        e.target ===
          this.overlay.querySelector(
            ".product-sheet-table__overlay-container"
          ) ||
        e.target ===
          this.overlay.querySelector(".product-sheet-table__overlay-frame")
      ) {
        this.closeOverlay();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.closeOverlay();
      }
    });

    window.addEventListener("resize", () => this.handleResize());
  }

  showOverlay(imageUrl) {
    console.log("Opening overlay with URL:", imageUrl);
    const formattedUrl = formatImageUrl(imageUrl);
    console.log("Formatted overlay URL:", formattedUrl);

    const img = new Image();

    img.onload = () => {
      console.log("Image loaded successfully");
      const aspectRatio = (img.naturalHeight / img.naturalWidth) * 100;
      const frame = this.overlay.querySelector(
        ".product-sheet-table__overlay-frame"
      );
      frame.style.paddingBottom = `${aspectRatio}%`;

      this.overlayImage.src = formattedUrl;
      this.overlay.classList.add("active");
      document.body.style.overflow = "hidden";
    };

    img.onerror = (error) => {
      console.error("Image load error:", error);
      console.error("Failed URL:", formattedUrl);
      this.closeOverlay();
    };

    img.src = formattedUrl;
  }

  closeOverlay() {
    this.overlay.classList.remove("active");
    document.body.style.overflow = "";
    setTimeout(() => {
      this.overlayImage.src = "";
    }, 300);
  }

  handleSort(column) {
    if (this.currentSort.column === column) {
      this.currentSort.direction =
        this.currentSort.direction === "asc" ? "desc" : "asc";
    } else {
      this.currentSort = { column, direction: "asc" };
    }

    this.sortData();
    this.renderTable();
    this.updateSortIndicators();
  }

  sortData() {
    const { column, direction } = this.currentSort;

    this.allData.sort((a, b) => {
      let valueA = a[column] || "";
      let valueB = b[column] || "";

      if (column === "dimensions") {
        valueA = parseInt(a.length) || 0;
        valueB = parseInt(b.length) || 0;
      } else if (column === "quantity") {
        valueA = valueA ? parseFloat(valueA) : 0;
        valueB = valueB ? parseFloat(valueB) : 0;
      } else if (!isNaN(valueA) && !isNaN(valueB)) {
        valueA = Number(valueA);
        valueB = Number(valueB);
      }

      if (typeof valueA === "string" && typeof valueB === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      if (valueA < valueB) return direction === "asc" ? -1 : 1;
      if (valueA > valueB) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }

  handleResize() {
    const isMobile = window.innerWidth <= 768;
    this.wrapper.classList.toggle("is-mobile", isMobile);
  }

  renderTable() {
    if (!this.allData.length) {
      this.showError("Out Of Stock.");
      if (this.toggleButton) this.toggleButton.style.display = "none";
      return;
    }

    const displayData = this.allData;

    this.tbody.innerHTML = displayData
      .map((item) => {
        const dimensions = (() => {
          if (item.length && item.width && item.thickness) {
            const length = parseFloat(item.length);
            const width = parseFloat(item.width);
            const [firstDim, secondDim] =
              width > length ? [width, length] : [length, width];
            return `${firstDim}x${secondDim}x${item.thickness} ${
              item.measurement_type || ""
            }`;
          }
          return "";
        })();

        const quantity = item["quantity_(m²)"] || "";

        const imageHtml = item.image
          ? `<img 
              src="${formatImageUrl(item.image)}" 
              alt="Product thumbnail" 
              class="product-sheet-table__thumbnail" 
              width="50" 
              height="50" 
              data-full-image="${formatImageUrl(item.image)}"
            >`
          : "";

        return `
        <tr>
          <td>${imageHtml}</td>
          <td>${item.bundle_no || ""}</td>
          <td class="product-sheet-table__mobile-hidden">${dimensions}</td>
          <td>${item.finish || ""}</td>
          <td class="product-sheet-table__mobile-hidden">${
            item.pieces || ""
          }</td>
          <td>${quantity}</td>
          <td>${item.status || ""}</td>
        </tr>
      `;
      })
      .join("");

    if (this.toggleButton) {
      this.toggleButton.style.display =
        this.allData.length > this.itemsPerPage ? "" : "none";
    }
    setTimeout(() => {
      this.adjustContainerHeight();
    }, 0);
  }

  updateSortIndicators() {
    const headers = this.table.querySelectorAll("th[data-sort]");

    headers.forEach((header) => {
      header.removeAttribute("data-sort-direction");

      if (header.dataset.sort === this.currentSort.column) {
        header.setAttribute("data-sort-direction", this.currentSort.direction);
      }
    });
  }
  adjustContainerHeight() {
    const tableHeight =
      this.tbody.offsetHeight + this.table.querySelector("thead").offsetHeight;
    const minHeight = 100; // Minimum height for the container
    const maxHeight = 500; // Maximum height for the container

    // Add some padding for aesthetics
    const newHeight = Math.min(
      Math.max(tableHeight + 20, minHeight),
      maxHeight
    );

    // Set the container height
    this.wrapper.style.height = `${newHeight}px`;
  }

  showError(message) {
    this.tbody.innerHTML = `
      <tr>
        <td colspan="8" style="text-align: center; padding: 2rem;">
          ${message}
        </td>
      </tr>
    `;
  }
}

function formatImageUrl(url) {
  if (!url) return "";

  try {
    console.log("Original URL:", url);

    // Handle different Google Drive URL formats
    if (url.includes("drive.google.com")) {
      let fileId;

      // Handle full sharing URL with parameters
      const fullMatch = url.match(/\/file\/d\/(.*?)\/.*?\?/);
      if (fullMatch) {
        fileId = fullMatch[1];
      } else {
        // Handle URL without parameters
        const simpleMatch = url.match(/\/file\/d\/(.*?)(\/|$)/);
        if (simpleMatch) {
          fileId = simpleMatch[1];
        }
      }

      if (fileId) {
        const formattedUrl = `https://lh3.googleusercontent.com/d/${fileId}`;
        console.log("Formatted URL:", formattedUrl);
        return formattedUrl;
      }
    }

    return url;
  } catch (error) {
    console.error("Error formatting image URL:", error);
    return url;
  }
}

// Initialize all product sheet tables when the DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll(".product-sheet-table-container")
    .forEach((wrapper) => {
      new ProductSheetTable(wrapper);
    });
});
